import { DashboardRequest } from '@/models';

/**
 * AB#21520 — the request behind "Export Order Prices".
 *
 * Reported as: exporting order data for Hummel Pro NA produced a file with no
 * **Mark to Factory** orders in it, while those same orders were sitting in the
 * list on screen.
 *
 * The cause is `items_for`. On the dashboard it is a *view* — which tab is
 * open — but on the wire the backend turns it into a hard status whitelist
 * (`OrderItemFiltersAndSearchTrait::applyFiltersAndSearch`, the
 * `$get_statuses_for` block). `order_approve`, the status the UI labels
 * "Mark to Factory", is not in the whitelist for `urgent_action_items`,
 * `pending_merchant_approval_items`, `customer_await_items`,
 * `quote_requested_items` or `completed_items` — so from any of those tabs the
 * export quietly loses every Mark to Factory row.
 *
 * Quietly is the part that made this a bug report rather than a preference: the
 * list does *not* narrow the same way (`AdminDashboardController` applies the
 * whitelist for six values of `items_for`, the export path applies it for ten),
 * there is no error, and a short spreadsheet looks exactly like a complete one.
 * Nothing on the screen or in the file says which orders were left out.
 *
 * So the rule here: **an export is over the orders the filters select, not over
 * the tab that happens to be open.** Every explicit filter still applies —
 * merchant, customer, factory, shop, order type, date ranges, the search term,
 * the Status filter and the quick-filter chips all travel in `quick_filters`
 * and are untouched. Only the tab's implicit stage scoping is dropped, by
 * pinning `items_for` to `all_items`, which is the one value the backend reads
 * as "do not filter by status".
 */
export const EXPORT_ITEMS_FOR = 'all_items';

/** The list's own defaults, for the first export of a fresh session. */
const EXPORT_DEFAULTS: DashboardRequest = {
  items_for: EXPORT_ITEMS_FOR,
  sort_by: 'order_no',
  sort_order: 'desc',
  page: null,
  per_page: null,
  search: '',
  new_dashboard: true,
};

/**
 * Build the query the export posts, from the filter state the list is rendered
 * from.
 *
 * `currentFilters` must be the **live** filter ref, not a re-read of
 * `localStorage['dashboardRequest']`. The saved copy is a lagging mirror:
 * `onTabChange()` never writes it, and `exportOrderPrices` in the store writes
 * its own `page: null` / `per_page: null` back into it, so an export built from
 * the saved copy can describe the screen as it was two interactions ago.
 *
 * `page` and `per_page` are cleared because the export is not paginated — the
 * endpoint runs the filtered query with `->get()`. Sending the list's page size
 * would be meaningless at best and a truncated file at worst.
 */
export const buildExportOrderParams = (
  currentFilters: Partial<DashboardRequest> | null | undefined
): DashboardRequest => {
  const { order_id: _deepLink, ...filters } = currentFilters ?? {};

  return {
    ...EXPORT_DEFAULTS,
    ...filters,
    // Last word, over both the defaults and the live filters: the tab is a
    // view, and pagination does not apply to a whole-result export.
    items_for: EXPORT_ITEMS_FOR,
    page: null,
    per_page: null,
  };
};
